import { SecurityCheckStatus } from 'types/common';
import { FileTypes } from 'utils/file-types';

/**
 * Which message family a linked document belongs to. The backend uses this both to filter the
 * document pool and to decide which outbound message may carry the file.
 */
export enum Ics2DocumentPurpose {
    RFI = 'RFI',
    RFS = 'RFS',
    RFI_HRCM = 'RFI_HRCM',
    FILING_HRCM = 'FILING_HRCM',
    AMENDMENT_HRCM = 'AMENDMENT_HRCM',
}

export interface Ics2UploadedFileInfo {
    purpose: Ics2DocumentPurpose;
    description?: string;
    mimeType: string;
    requestNotificationId?: string;
    referralRequestReference?: string;
}

export interface Ics2DocumentRequest {
    fileId: string;
    fileReference: string;
    fileName: string;
    /** Required when updating; omitted on create. A stale value is rejected with 409. */
    metadataVersion?: number;
    fileInfo: Ics2UploadedFileInfo;
}

export interface Ics2DocumentFilters {
    process?: Ics2DocumentPurpose;
    referralRequestReference?: string;
    requestNotificationId?: string;
}

export enum Ics2UploadedFileStatus {
    UPLOADED = 'UPLOADED',
    DELETING = 'DELETING',
    SUBMITTED = 'SUBMITTED',
    REJECTED = 'REJECTED',
    SUCCESS = 'SUCCESS',
}

export interface Ics2UploadedFile {
    id: string; // uploaded_files.id` - this is what goes into a submission's `documentVersions
    fileId: string;
    metadataVersion: number;
    fileReference?: string;
    fileName: string;
    declarationId?: string;
    status: Ics2UploadedFileStatus;
    fileInfo?: Ics2UploadedFileInfo;
    createdAt?: string;
    createdBy?: string;
    securityChecksStatus?: SecurityCheckStatus;
}

export const isSelectableIcs2Document = (file: Ics2UploadedFile): boolean =>
    file.status === Ics2UploadedFileStatus.UPLOADED || file.status === Ics2UploadedFileStatus.REJECTED;

/**
 * Customs only accepts evidence whose CMS scan came back clean; anything else is a 400 on submit.
 *
 * A missing status is treated as sendable - only rows predating the scan ever have one, and the
 * backend rechecks the live CMS status anyway.
 */
export const isIcs2DocumentSendable = (file: Ics2UploadedFile): boolean =>
    !file.securityChecksStatus || file.securityChecksStatus === 'VERIFIED';

export const ICS2_ATTACHMENT_FILE_TYPES: FileTypes[] = [FileTypes.PDF, FileTypes.JPG, FileTypes.JPEG];

export const ICS2_MAX_ATTACHMENTS_PER_REFERRAL = 9;

export const getIcs2ReferralDocumentPurpose = (isScreening: boolean): Ics2DocumentPurpose =>
    isScreening ? Ics2DocumentPurpose.RFS : Ics2DocumentPurpose.RFI;
